import { useEffect } from 'react';
import { Switch, Route, Redirect, RouteComponentProps } from 'react-router-dom';
import * as Building from '.';
import { useParams } from 'util/reactRouterHooks';
import { HasUrlSlug } from 'selectors/buildingsSelectors';
import { getBuildingByUrlSlug } from 'selectors/buildingsSelectors';
import { BuildingSettingsRouter } from './settings/buildingSettingsRouter/BuildingSettingsRouter';
import { useSession, useMenu, MenuItem } from 'hooks';
import { Permission } from 'shared/permissions';
import { ElevatorListPage } from './elevatorList/elevatorListPage/ElevatorListPage';
import { useSelector } from 'react-redux';
import { State } from 'reducers';

export type Props = RouteComponentProps<any>;

export const BuildingRouter = () => {
    const params = useParams<HasUrlSlug>();
    const { urlSlug } = params;

    const { hasPermission } = useSession();
    const { pushMenu, popMenu } = useMenu();

    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const hasEcs = building && building.hasEcs;
    const hasBroadcast = building && building.hasBroadcast;
    const buildingId = building && building.id;

    //
    // Create/Update the buildings menu when the BuildingRouter is
    // mounted, the building changes, or the user permissions change
    //
    useEffect(() => {
        const items: MenuItem[] = [];

        if (hasEcs) {
            items.push({ id: 'building-elevators-link', link: `/buildings/${urlSlug}/elevators`, textKey: 'BUILDING.ELEVATORS.LINK' });
        }

        items.push({ id: 'building-devices-link', link: `/buildings/${urlSlug}/devices`, textKey: 'BUILDING.DEVICES.LINK' });
        items.push({ id: 'building-themes-link',  link: `/buildings/${urlSlug}/themes`,  textKey: 'BUILDING.THEMES.LINK' });
        items.push({ id: 'building-events-link',  link: `/buildings/${urlSlug}/events`,  textKey: 'BUILDING.EVENT_LOG.LINK' });

        if (hasBroadcast) {
            items.push({ id: 'building-broadcast-link', link: `/buildings/${urlSlug}/broadcast`, textKey: 'BUILDING.BROADCAST.LINK' });
        }

        if (hasPermission(buildingId, Permission.EditBuilding) || hasPermission(buildingId, Permission.EditBuildingFeatures)) {
            items.push({ id: 'building-settings-link', link: `/buildings/${urlSlug}/settings`, textKey: 'BUILDING.SETTINGS.LINK' });
        }

        pushMenu({ id: 'menu-building', textKey: 'BUILDING.TITLE', items }, 1);

        // Remove the menu when we are unmounted
        return () => popMenu(1);
    }, [ urlSlug, hasEcs, hasBroadcast, buildingId, pushMenu, popMenu, hasPermission ]);

    return (
        <Switch>
            { hasPermission(buildingId, Permission.ViewDevices) &&
                <Route path="/buildings/:urlSlug/devices/:deviceId/:devicePage" component={ Building.BuildingDevicesPage }/>
            }
            { hasPermission(buildingId, Permission.ViewDevices) &&
                <Route path="/buildings/:urlSlug/devices/:deviceId" component={ Building.BuildingDevicesPage }/>
            }
            { hasPermission(buildingId, Permission.ViewDevices) &&
                <Route path="/buildings/:urlSlug/elevators" component={ ElevatorListPage }/>
            }
            { hasPermission(buildingId, Permission.ViewDevices) &&
                <Route path="/buildings/:urlSlug/devices" component={ Building.BuildingDevicesPage }/>
            }
            { hasPermission(buildingId, Permission.ViewThemes) &&
                <Route path="/buildings/:urlSlug/themes" component={ Building.BuildingThemesPage }/>
            }
            { hasPermission(buildingId, Permission.ViewDevices) &&
                <Route path="/buildings/:urlSlug/events" component={ Building.BuildingEventsPage } />
            }
            { hasPermission(buildingId, Permission.Broadcast) &&
                <Route path="/buildings/:urlSlug/broadcast" component={ Building.BuildingBroadcastPage }/>
            }
            { (hasPermission(buildingId, Permission.EditBuilding) || hasPermission(buildingId, Permission.EditBuildingFeatures)) &&
                <Route path="/buildings/:urlSlug/settings" component={ BuildingSettingsRouter }/>
            }

            <Redirect to={ `/buildings/${urlSlug}/devices` }/>
        </Switch>
    );
};
